import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  MenuItem,
  TextField,
  Tooltip,
  Paper,
  Button,
  Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  Satellite as SatelliteIcon,
  TrendingUp as TrendingUpIcon,
  WifiOff as WifiOffIcon,
  Schedule as ScheduleIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { API } from '../services/api';
import { showSuccess, showError } from '../utils/notifications';
import DataTable from '../components/DataTable';
import TableSkeleton from '../components/TableSkeleton';

const MotionBox = motion(Box);
const MotionPaper = motion(Paper);

function SensorData() {
  const [sensorData, setSensorData] = useState([]);
  const [projects, setProjects] = useState([]);
  const [devices, setDevices] = useState([]);
  const [models, setModels] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Filter states
  const [selectedProject, setSelectedProject] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [deviceFilter, setDeviceFilter] = useState('all');
  const [qualityFilter, setQualityFilter] = useState('all');
  const [anomalyFilter, setAnomalyFilter] = useState('all');

  const fetchInitialData = async () => {
    try {
      setInitialLoading(true);
      const [projectsRes, devicesRes, modelsRes, equipmentRes, unitsRes] = await Promise.all([
        API.projects.list(),
        API.iotDevices.list(),
        API.models.list(),
        API.equipment.list(),
        API.units.list(),
      ]);
      
      setProjects(projectsRes?.data || []);
      setDevices(devicesRes?.data || []);
      setModels(modelsRes?.data || []);
      setEquipment(equipmentRes?.data || []);
      setUnits(unitsRes?.data || []);
    } catch (err) {
      showError('Failed to load initial data');
      console.error('Initial data fetch error:', err);
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  const handleSearch = async () => {
    if (!selectedProject) {
      showError('Please select a project');
      return;
    }
    if (!startDate || !endDate) {
      showError('Please select both start and end dates');
      return;
    }

    try {
      setLoading(true);
      setHasSearched(true);
      
      const params: any = {
        project_id: selectedProject,
        start_date: startDate,
        end_date: endDate,
      };
      
      if (deviceFilter !== 'all') params.device_id = deviceFilter;

      const dataRes = await API.sensorData.list(params);
      setSensorData(dataRes?.data || []);
      
      if (dataRes?.data?.length === 0) {
        showSuccess('No data found for the selected criteria');
      } else {
        showSuccess(`Loaded ${dataRes.data.length} sensor records`);
      }
    } catch (err) {
      showError('Failed to load sensor data');
      console.error('Sensor data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDeviceName = (deviceId) => {
    const device = devices.find(d => d.device_id === deviceId);
    return device ? device.device_name : 'Unknown';
  };

  const getModelName = (modelId) => {
    const model = models.find(m => m.model_id === modelId);
    return model ? model.model_name : 'Unknown';
  };

  const getEquipmentName = (equipmentId) => {
    if (!equipmentId) return '-';
    const eq = equipment.find(e => e.equipment_id === equipmentId);
    return eq ? eq.equipment_name : 'Unknown';
  };

  const getUnitSymbol = (unitId) => {
    const unit = units.find(u => u.unit_id === unitId);
    return unit ? unit.unit_symbol : '';
  };

  const getQualityColor = (quality) => {
    switch (quality) {
      case 1: return { bg: '#D1FAE5', color: '#065F46', border: '#6EE7B7' }; // Good - Green
      case 2: return { bg: '#FEF3C7', color: '#92400E', border: '#FCD34D' }; // Fair - Yellow
      case 3: return { bg: '#FEE2E2', color: '#991B1B', border: '#FCA5A5' }; // Poor - Red
      default: return { bg: '#F3F4F6', color: '#6B7280', border: '#D1D5DB' };
    }
  };

  const getQualityLabel = (quality) => {
    switch (quality) {
      case 1: return 'Good';
      case 2: return 'Fair';
      case 3: return 'Poor';
      default: return 'Unknown';
    }
  };

  const getAlertLevelColor = (level) => {
    switch (level) {
      case 1: return { bg: '#FEE2E2', color: '#991B1B', border: '#FCA5A5' }; // Critical - Red
      case 2: return { bg: '#FEE2E2', color: '#991B1B', border: '#FCA5A5' }; // High - Red
      case 3: return { bg: '#FEF3C7', color: '#92400E', border: '#FCD34D' }; // Medium - Yellow
      case 4: return { bg: '#DBEAFE', color: '#1E40AF', border: '#93C5FD' }; // Low - Blue
      default: return { bg: '#F3F4F6', color: '#6B7280', border: '#D1D5DB' };
    }
  };

  const getAlertLevelLabel = (level) => {
    switch (level) {
      case 1: return 'Critical';
      case 2: return 'High';
      case 3: return 'Medium';
      case 4: return 'Low';
      default: return '-';
    }
  };

  const filteredData = sensorData.filter(data => {
    const matchesQuality = qualityFilter === 'all' || data.data_quality === parseInt(qualityFilter);
    const matchesAnomaly = anomalyFilter === 'all' || 
      (anomalyFilter === 'true' && data.is_anomaly) ||
      (anomalyFilter === 'false' && !data.is_anomaly);
    
    return matchesQuality && matchesAnomaly;
  });

  const columns = [
    {
      field: 'device_id',
      headerName: 'Device',
      width: '200px',
      render: (row) => {
        const deviceName = getDeviceName(row.device_id);
        return (
          <Tooltip title={deviceName} placement="top" arrow>
            <Typography 
              variant="body2" 
              fontWeight={600}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '200px',
              }}
            >
              {deviceName}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'model_id',
      headerName: 'Model',
      width: '180px',
      render: (row) => {
        const modelName = getModelName(row.model_id);
        return (
          <Tooltip title={modelName} placement="top" arrow>
            <Typography 
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '180px',
              }}
            >
              {modelName}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'equipment_id',
      headerName: 'Equipment',
      width: '150px',
      render: (row) => getEquipmentName(row.equipment_id),
    },
    {
      field: 'source_system',
      headerName: 'Source System',
      width: '150px',
      render: (row) => row.source_system || '-',
    },
    {
      field: 'online_status',
      headerName: 'Online Status',
      width: '110px',
      render: (row) => (
        <Chip
          label={row.online_status ? 'Online' : 'Offline'}
          size="small"
          sx={{ 
            fontWeight: 600,
            fontSize: '0.7rem',
            backgroundColor: row.online_status ? '#D1FAE5' : '#F3F4F6',
            color: row.online_status ? '#065F46' : '#6B7280',
            border: `1px solid ${row.online_status ? '#6EE7B7' : '#D1D5DB'}`,
          }}
        />
      ),
    },
    {
      field: 'device_status',
      headerName: 'Device Status',
      width: '120px',
      render: (row) => row.device_status || '-',
    },
    {
      field: 'external_device_id',
      headerName: 'External Device ID',
      width: '180px',
      render: (row) => (
        <Tooltip title={row.external_device_id || '-'} placement="top" arrow>
          <Typography 
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '180px',
            }}
          >
            {row.external_device_id || '-'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'timestamp',
      headerName: 'Timestamp',
      width: '170px',
      render: (row) => row.timestamp ? new Date(row.timestamp).toLocaleString() : '-',
    },
    {
      field: 'value',
      headerName: 'Value',
      width: '100px',
      render: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {row.value !== null && row.value !== undefined ? row.value : '-'} {row.value !== null && row.value !== undefined ? getUnitSymbol(row.unit_id) : ''}
        </Typography>
      ),
    },
    {
      field: 'unit_id',
      headerName: 'Unit',
      width: '80px',
      render: (row) => getUnitSymbol(row.unit_id) || '-',
    },
    {
      field: 'data_quality',
      headerName: 'Quality',
      width: '90px',
      render: (row) => {
        const colors = getQualityColor(row.data_quality);
        return (
          <Chip
            label={getQualityLabel(row.data_quality)}
            size="small"
            sx={{ 
              fontWeight: 600,
              fontSize: '0.7rem',
              backgroundColor: colors.bg,
              color: colors.color,
              border: `1px solid ${colors.border}`,
            }}
          />
        );
      },
    },
    {
      field: 'is_anomaly',
      headerName: 'Anomaly',
      width: '90px',
      render: (row) => (
        <Chip
          label={row.is_anomaly ? 'Yes' : 'No'}
          size="small"
          sx={{ 
            fontWeight: 600,
            fontSize: '0.7rem',
            backgroundColor: row.is_anomaly ? '#FEE2E2' : '#D1FAE5',
            color: row.is_anomaly ? '#991B1B' : '#065F46',
            border: `1px solid ${row.is_anomaly ? '#FCA5A5' : '#6EE7B7'}`,
          }}
        />
      ),
    },
    {
      field: 'alert_level',
      headerName: 'Alert',
      width: '100px',
      render: (row) => {
        if (!row.alert_level) return '-';
        const colors = getAlertLevelColor(row.alert_level);
        return (
          <Chip
            label={getAlertLevelLabel(row.alert_level)}
            size="small"
            sx={{ 
              fontWeight: 600,
              fontSize: '0.7rem',
              backgroundColor: colors.bg,
              color: colors.color,
              border: `1px solid ${colors.border}`,
            }}
          />
        );
      },
    },
    {
      field: 'power_state',
      headerName: 'Power State',
      width: '110px',
      render: (row) => row.power_state || '-',
    },
    {
      field: 'temperature',
      headerName: 'Temperature',
      width: '110px',
      render: (row) => row.temperature !== null && row.temperature !== undefined ? `${row.temperature}°C` : '-',
    },
    {
      field: 'mode',
      headerName: 'Mode',
      width: '100px',
      render: (row) => row.mode || '-',
    },
    {
      field: 'fan_speed',
      headerName: 'Fan Speed',
      width: '100px',
      render: (row) => row.fan_speed || '-',
    },
    {
      field: 'vane',
      headerName: 'Vane',
      width: '90px',
      render: (row) => row.vane || '-',
    },
    {
      field: 'brown_energy',
      headerName: 'Brown Energy',
      width: '120px',
      render: (row) => row.brown_energy !== null && row.brown_energy !== undefined ? `${row.brown_energy} kWh` : '-',
    },
    {
      field: 'solar_generated',
      headerName: 'Solar Gen',
      width: '110px',
      render: (row) => row.solar_generated !== null && row.solar_generated !== undefined ? `${row.solar_generated} kWh` : '-',
    },
    {
      field: 'solar_export',
      headerName: 'Solar Export',
      width: '110px',
      render: (row) => row.solar_export !== null && row.solar_export !== undefined ? `${row.solar_export} kWh` : '-',
    },
    {
      field: 'total_consumption',
      headerName: 'Total Consumption',
      width: '150px',
      render: (row) => row.total_consumption !== null && row.total_consumption !== undefined ? `${row.total_consumption} kWh` : '-',
    },
    {
      field: 'data_payload',
      headerName: 'Data Payload',
      width: '150px',
      render: (row) => (
        <Tooltip title={row.data_payload ? JSON.stringify(row.data_payload, null, 2) : 'No payload'} placement="top" arrow>
          <Typography 
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '150px',
              cursor: 'pointer',
            }}
          >
            {row.data_payload ? 'View JSON' : '-'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: '170px',
      render: (row) => row.created_at ? new Date(row.created_at).toLocaleString() : '-',
    },
    {
      field: 'updated_at',
      headerName: 'Updated At',
      width: '170px',
      render: (row) => row.updated_at ? new Date(row.updated_at).toLocaleString() : '-',
    },
  ];

  if (initialLoading) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
            IoT Sensor Data
          </Typography>
        </Box>
        <TableSkeleton columns={8} rows={10} />
      </Box>
    );
  }

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Hero Header */}
      <MotionBox
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
        sx={{
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
          borderRadius: 4,
          p: 4,
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(99, 102, 241, 0.3)',
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <motion.div
              animate={{
                rotate: [0, 360],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              <SatelliteIcon sx={{ fontSize: 48, color: 'white' }} />
            </motion.div>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: 'white', mb: 0.5 }}>
                IoT Sensor Data
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                Real-time monitoring of millions of sensor data points
              </Typography>
            </Box>
          </Box>
          
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ 
                background: 'rgba(255,255,255,0.15)', 
                backdropFilter: 'blur(10px)',
                p: 2, 
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.2)',
              }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: 'white' }}>
                  {sensorData.length.toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Total Records
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ 
                background: 'rgba(255,255,255,0.15)', 
                backdropFilter: 'blur(10px)',
                p: 2, 
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.2)',
              }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: 'white' }}>
                  {devices.length}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Active Devices
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ 
                background: 'rgba(255,255,255,0.15)', 
                backdropFilter: 'blur(10px)',
                p: 2, 
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.2)',
              }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: 'white' }}>
                  {projects.length}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Projects
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ 
                background: 'rgba(255,255,255,0.15)', 
                backdropFilter: 'blur(10px)',
                p: 2, 
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.2)',
              }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: 'white' }}>
                  {filteredData.filter(d => d.is_anomaly).length}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Anomalies Detected
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </MotionBox>

      {/* Search and Filter Section */}
      <MotionPaper
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          border: '2px solid rgba(99, 102, 241, 0.1)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <FilterListIcon sx={{ fontSize: 28, color: '#6366f1' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
            Filter Sensor Data
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Project *"
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: '#6366f1' },
                  '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                },
              }}
            >
              <MenuItem value="">Select Project</MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.project_id} value={project.project_id}>
                  {project.project_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Start Date *"
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: '#6366f1' },
                  '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="End Date *"
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: '#6366f1' },
                  '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
                sx={{
                  height: '40px',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  color: 'white',
                  fontWeight: 700,
                  boxShadow: '0 4px 16px rgba(99, 102, 241, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                    boxShadow: '0 6px 24px rgba(99, 102, 241, 0.4)',
                  },
                  '&:disabled': {
                    background: '#e0e0e0',
                  },
                }}
              >
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Device Filter"
              value={deviceFilter}
              onChange={(e) => setDeviceFilter(e.target.value)}
              size="small"
            >
              <MenuItem value="all">All Devices</MenuItem>
              {devices.map((device) => (
                <MenuItem key={device.device_id} value={device.device_id}>
                  {device.device_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Data Quality"
              value={qualityFilter}
              onChange={(e) => setQualityFilter(e.target.value)}
              size="small"
            >
              <MenuItem value="all">All Quality</MenuItem>
              <MenuItem value="1">Good</MenuItem>
              <MenuItem value="2">Fair</MenuItem>
              <MenuItem value="3">Poor</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Anomaly Filter"
              value={anomalyFilter}
              onChange={(e) => setAnomalyFilter(e.target.value)}
              size="small"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="true">Anomalies Only</MenuItem>
              <MenuItem value="false">Normal Only</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </MotionPaper>

      {/* Data Display Section */}
      <AnimatePresence mode="wait">
        {!hasSearched ? (
          <MotionPaper
            key="empty-state"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.4 }}
            elevation={0}
            sx={{
              textAlign: 'center',
              py: 12,
              background: 'linear-gradient(135deg, #f8f9ff 0%, #fef5ff 100%)',
              borderRadius: 4,
              border: '3px dashed rgba(99, 102, 241, 0.25)',
            }}
          >
            <motion.div
              animate={{
                y: [0, -20, 0],
                rotate: [0, 10, -10, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <SatelliteIcon sx={{ fontSize: 120, color: 'rgba(99, 102, 241, 0.3)', mb: 3 }} />
            </motion.div>
            
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#6366f1', mb: 2 }}>
              Ready to Explore Sensor Data
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1, maxWidth: 600, mx: 'auto' }}>
              Select a <strong>project</strong> and <strong>date range</strong> to begin analyzing your IoT sensor data
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
              Our system handles millions of data points efficiently. Use the filters above to narrow down your search and get precise insights.
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mt: 4 }}>
              <Box sx={{ textAlign: 'center' }}>
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <ScheduleIcon sx={{ fontSize: 48, color: '#6366f1', mb: 1 }} />
                </motion.div>
                <Typography variant="caption" color="text.secondary">
                  Time-based Queries
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                >
                  <TrendingUpIcon sx={{ fontSize: 48, color: '#8b5cf6', mb: 1 }} />
                </motion.div>
                <Typography variant="caption" color="text.secondary">
                  Real-time Analytics
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                >
                  <WifiOffIcon sx={{ fontSize: 48, color: '#ec4899', mb: 1 }} />
                </motion.div>
                <Typography variant="caption" color="text.secondary">
                  Anomaly Detection
                </Typography>
              </Box>
            </Box>
          </MotionPaper>
        ) : loading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <TableSkeleton columns={8} rows={10} />
          </motion.div>
        ) : (
          <MotionBox
            key="data-table"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
          >
            <DataTable
              columns={columns}
              data={filteredData}
              getRowId={(row) => row.data_id}
              emptyMessage="No sensor data found for the selected criteria"
              disableActions
            />
          </MotionBox>
        )}
      </AnimatePresence>
    </MotionBox>
  );
}

export default SensorData;
